import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, UsersRound } from 'lucide-react';

const MotionLink = motion(Link);

// "We're not a marketplace. We're the roofer." — the founding-team /
// who-we-are section that replaced InstantEstimateHero on 2026-05-21.
// Two-column on desktop: copy + 3 bullets on the left, founding-team grid
// (3×2) on the right.
//
// Choreography (left column): wrapped in a single motion.div with
// `initial="hidden" whileInView="visible"` so the entire sequence plays
// out from one trigger point regardless of scroll speed. Per-child
// variants drive the per-element timings and motion vocabularies:
//   eyebrow   — slide from left (-16px x), fade,  duration 0.4,  delay 0
//   line 1    — fade up   (+24px y),         duration 0.45, delay 0.15
//   line 2    — fade up   (+24px y),         duration 0.45, delay 0.30
//   body      — fade only (no slide),        duration 0.50, delay 0.95
//                ↑ 200ms after line 2 completes (0.30 + 0.45 + 0.20)
//   bullet i  — slide from left (-32px),     duration 0.50,
//                delay 1.15 + i * 0.20
//   check i   — pulse [1, 1.1, 1] + cyan drop-shadow brighten then dim,
//                delay landingDelay + 0.40 (so the pulse peaks as the
//                card finishes settling)
//
// Hover (per bullet): card lifts 3px, border flips to cyan, soft cyan
// glow appears, green disc scales to 1.08. Hover state is held in React
// state so the disc scale doesn't fight the entrance pulse via variant
// re-entry.
//
// Continuous checkmark breath (~5s ambient) — SKIPPED. With the call
// bubble + address card + video card all breathing cyan, three more
// pulsing discs would tip the homepage from "premium" into "busy".
// Easy to add back via a separate motion element if the absence reads
// as too static.
//
// Team grid (right column): independent of the left-column orchestration
// — its own per-card whileInView stagger so the grid reveal kicks off
// from its own trigger point, not the left column's.

// 7 team members in row-major order for the 4×2 desktop grid below. The
// 8th cell (row 2, col 4) is the "Meet the full team" CTA, not a team
// member. Ty was added 2026-05-21 (Chief Financial & Strategy Officer);
// Leah Raffles and Lyric Baker live on the About page only.
//   row 1: Mike     | Patrick | Alex   | Ty
//   row 2: Chuck    | Angel   | Justin | [CTA → /about]
const teamMembers = [
  { name: 'Mike Chesbrough',  title: 'Co-CEO', photo: '/assets/images/unnamed_5_-1773084503687.jpg' },
  { name: 'Patrick Salyers',  title: 'Co-CEO', photo: '/assets/images/unnamed_4_-1773084625487.jpg' },
  { name: 'Alex Skelton',     title: 'COO',    photo: '/assets/images/unnamed_3_-1773084973084.png' },
  { name: 'Ty Adams',         title: 'CFSO',   photo: '/images/team/ty-adams.webp' },
  { name: 'Chuck Gamkong',    title: 'CTO',    photo: '/assets/images/unnamed_3_-1773085416010.jpg' },
  { name: 'Angel Kimble',     title: 'CLO',    photo: '/assets/images/unnamed_2_-1773084995800.png' },
  { name: 'Justin Fahey',     title: 'CMO',    photo: '/assets/images/unnamed_2_-1773085079525.jpg' },
];

// Smooth ease-out cubic — same curve FadeIn uses across the site.
const EASE_OUT = [0.16, 1, 0.3, 1];

// Card stagger for the right-column team grid (unchanged from prior turn).
const CARD_STAGGER_STEP = 0.15;
const CARD_BASE_DELAY = 0.48;

const TeamCard = ({ member, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ y: -4 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{
      opacity: { duration: 0.55, delay: CARD_BASE_DELAY + index * CARD_STAGGER_STEP, ease: EASE_OUT },
      y: { duration: 0.25, ease: 'easeOut' },
    }}
    className="group bg-white rounded-2xl p-5 flex flex-col items-center text-center border border-gray-100 shadow-sm hover:shadow-lg transition-[box-shadow,border-color] duration-300"
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#07B0E9'; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = ''; }}
  >
    <div
      className="w-20 h-20 rounded-full overflow-hidden mb-3 ring-1 ring-[#07B0E9]/15 group-hover:ring-[#07B0E9]/45 transition-shadow"
      style={{ background: 'linear-gradient(135deg, rgba(7,176,233,0.10), rgba(7,176,233,0.04))' }}
    >
      <img
        src={member.photo}
        alt={`${member.name} headshot`}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
      />
    </div>
    <h3 className="font-bold text-[15px] leading-tight" style={{ color: '#000150' }}>
      {member.name}
    </h3>
    <p className="text-[13px] text-gray-500 mt-1">{member.title}</p>
  </motion.div>
);

// "Meet the full team" CTA card — sits in the 8th cell of the 4×2 grid
// (row 2, col 4). Same shape + size as TeamCard so the grid reads as
// symmetrical, but with a UsersRound icon instead of a photo and a subtle
// cyan tint to differentiate it as a navigation affordance. Stagger-fade
// in via the same per-index delay as the team cards. Whole card is a Link
// to /about. Arrow icon under the subhead slides 4px right on hover.
const FullTeamCtaCard = ({ index }) => (
  <MotionLink
    to="/about"
    aria-label="See the full OpenbooQ team on the About page"
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ y: -4 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{
      opacity: { duration: 0.55, delay: CARD_BASE_DELAY + index * CARD_STAGGER_STEP, ease: EASE_OUT },
      y: { duration: 0.25, ease: 'easeOut' },
    }}
    className="group rounded-2xl p-5 flex flex-col items-center text-center border shadow-sm hover:shadow-lg transition-[background-color,box-shadow,border-color] duration-300 no-underline"
    style={{
      background: 'rgba(7, 176, 233, 0.06)',
      borderColor: 'rgba(7, 176, 233, 0.25)',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'rgba(7, 176, 233, 0.14)';
      e.currentTarget.style.borderColor = '#07B0E9';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'rgba(7, 176, 233, 0.06)';
      e.currentTarget.style.borderColor = 'rgba(7, 176, 233, 0.25)';
    }}
  >
    <div
      className="w-20 h-20 rounded-full flex items-center justify-center mb-3 ring-1 ring-[#07B0E9]/25 group-hover:ring-[#07B0E9]/55 transition-shadow"
      style={{ background: 'linear-gradient(135deg, rgba(7,176,233,0.15), rgba(7,176,233,0.05))' }}
    >
      <UsersRound size={48} strokeWidth={1.75} style={{ color: '#07B0E9' }} aria-hidden="true" />
    </div>
    <h3 className="font-bold text-[15px] leading-tight" style={{ color: '#000150' }}>
      Meet the full team
    </h3>
    <p className="text-[13px] text-gray-500 mt-1 flex items-center justify-center gap-1.5">
      <span>See the people behind OpenbooQ</span>
      <ArrowRight
        size={14}
        strokeWidth={2.25}
        className="transition-transform duration-300 ease-out group-hover:translate-x-1"
        style={{ color: '#07B0E9' }}
        aria-hidden="true"
      />
    </p>
  </MotionLink>
);

// Variants for the left-column orchestration. Parent activates "visible"
// when scrolled into view; each named child variant carries its own delay
// + transition so the sequence plays out from a single trigger.
const containerVariants = {
  hidden: {},
  visible: {},
};

const eyebrowVariants = {
  hidden: { opacity: 0, x: -16 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, delay: 0, ease: EASE_OUT },
  },
};

const headlineLine1Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: 0.15, ease: EASE_OUT },
  },
};

const headlineLine2Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: 0.30, ease: EASE_OUT },
  },
};

const bodyVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.5, delay: 0.95, ease: EASE_OUT },
  },
};

// Per-bullet entrance + landing-pulse delays. Variant functions consume
// the `custom` prop (index) so each bullet + its checkmark stagger from
// the parent container's single trigger point.
const BULLET_BASE_DELAY = 1.15;
const BULLET_STAGGER_STEP = 0.20;
const PULSE_RELATIVE_DELAY = 0.40;

const cardEntranceVariants = {
  hidden: { opacity: 0, x: -32 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, delay: BULLET_BASE_DELAY + i * BULLET_STAGGER_STEP, ease: EASE_OUT },
  }),
};

const checkPulseVariants = {
  hidden: { scale: 1, filter: 'drop-shadow(0 0 0px rgba(7, 176, 233, 0))' },
  visible: (i) => ({
    scale: [1, 1.1, 1],
    filter: [
      'drop-shadow(0 0 0px rgba(7, 176, 233, 0))',
      'drop-shadow(0 0 14px rgba(7, 176, 233, 0.65))',
      'drop-shadow(0 0 6px rgba(7, 176, 233, 0.20))',
    ],
    transition: {
      duration: 0.45,
      delay: BULLET_BASE_DELAY + i * BULLET_STAGGER_STEP + PULSE_RELATIVE_DELAY,
      ease: 'easeOut',
    },
  }),
};

const Bullet = ({ boldText, restText, index }) => {
  const [hovered, setHovered] = useState(false);

  // Card entrance via variants (inherits "visible" from parent container's
  // single trigger). On hover, lifts 3px and gets a soft cyan glow + cyan
  // border. Border is held in state-driven inline style so it composes
  // cleanly with motion's transform (motion owns transform; CSS owns border).
  return (
    <motion.div
      variants={cardEntranceVariants}
      custom={index}
      whileHover={{ y: -3, boxShadow: '0 12px 32px rgba(7, 176, 233, 0.28)' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      transition={{ y: { duration: 0.25, ease: 'easeOut' }, boxShadow: { duration: 0.25, ease: 'easeOut' } }}
      className="flex items-start gap-3 bg-white border rounded-xl p-4 shadow-sm cursor-default"
      style={{
        borderColor: hovered ? '#07B0E9' : '#F3F4F6',
        transition: 'border-color 0.25s ease-out',
      }}
    >
      {/* Outer wrapper: hover-driven scale on the green disc via plain CSS
          transform. Composes with the inner pulse (parent_scale * inner_scale). */}
      <div
        className="flex-shrink-0 mt-0.5"
        style={{
          transform: `scale(${hovered ? 1.08 : 1})`,
          transformOrigin: 'center',
          transition: 'transform 0.25s ease-out',
        }}
      >
        {/* Inner: green disc with landing pulse (scale + cyan drop-shadow
            brighten then dim). Variant fires once when parent goes to
            "visible". Once-true viewport on the parent prevents replay. */}
        <motion.span
          variants={checkPulseVariants}
          custom={index}
          className="flex items-center justify-center w-6 h-6 rounded-full"
          style={{ background: '#10B981' }}
          aria-hidden="true"
        >
          <Check size={14} strokeWidth={3} className="text-white" />
        </motion.span>
      </div>
      <p className="text-sm leading-relaxed text-gray-700">
        <strong className="font-bold" style={{ color: '#000150' }}>{boldText}</strong> {restText}
      </p>
    </motion.div>
  );
};

const WhoWeAreSection = () => (
  <section className="py-16 lg:py-24" style={{ background: '#F8FAFC' }}>
    <div className="max-w-[1400px] mx-auto px-5 lg:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

        {/* LEFT: orchestrated reveal — eyebrow → headline → body → bullets */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          <motion.div
            variants={eyebrowVariants}
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] mb-4"
            style={{ color: '#07B0E9' }}
          >
            <span className="w-6 h-px" style={{ background: '#07B0E9', opacity: 0.6 }} aria-hidden="true" />
            Who We Are
          </motion.div>

          {/* Two-line headline. Each line is its own motion.span (display:block)
              so they animate independently — setup ("We're not a marketplace.")
              then punchline ("We're the roofer.") 150ms apart. */}
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-black mb-5 leading-[1.1] tracking-tight"
            style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.02em' }}
          >
            <motion.span variants={headlineLine1Variants} style={{ display: 'block' }}>
              We're not a marketplace.
            </motion.span>
            <motion.span
              variants={headlineLine2Variants}
              style={{ display: 'block', color: '#07B0E9' }}
            >
              We're the roofer.
            </motion.span>
          </h2>

          <motion.p
            variants={bodyVariants}
            className="text-gray-600 text-base lg:text-lg leading-relaxed mb-7 max-w-xl"
          >
            OpenbooQ isn't lead-gen. We don't sell your info to twenty roofers who'll blow up your phone for a month. Six co-founders in Dayton built this company — and we install your roof ourselves.
          </motion.p>

          <div className="space-y-3">
            <Bullet
              boldText="One company, end to end."
              restText="We quote it, schedule it, install it, warranty it. No referrals. No middlemen."
              index={0}
            />
            <Bullet
              boldText="No lead-selling."
              restText="Your address stays with us. We never sell, share, or resell your info."
              index={1}
            />
            <Bullet
              boldText="Manufacturer-certified crews."
              restText="Body-cam recorded on every install. Same certifications, same materials, better process."
              index={2}
            />
          </div>
        </motion.div>

        {/* RIGHT: founding-team grid + "Meet the full team" CTA — 4 cols ≥1024,
            2 cols 640-1023, 1 col below. Skipping a 3-col breakpoint keeps the
            grid symmetric (8 items: 4×2 or 2×4 or 1×8, never with orphans). */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {teamMembers.map((member, i) => (
            <TeamCard key={member.name} member={member} index={i} />
          ))}
          <FullTeamCtaCard index={teamMembers.length} />
        </div>
      </div>
    </div>
  </section>
);

export default WhoWeAreSection;
